import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from "./apiServices";
import { parseJwt, persistAuth, clearAuthStorage } from "../utilities/auth";
import { store } from "../redux/store";
import { loginSuccess, logout } from "../redux/slices/userSlice";
import { getToken } from "../utilities/auth";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

let logoutTimer = null;

function scheduleLogout(expiresAtMs) {
  if (logoutTimer) clearTimeout(logoutTimer);
  const ms = expiresAtMs - Date.now();
  if (ms > 0) {
    logoutTimer = setTimeout(() => {
      clearAuthStorage();
      store.dispatch(logout());
    }, ms);
  } else {
    // already expired
    clearAuthStorage();
    store.dispatch(logout());
  }
}

// Helper to determine role from roles array
function determineRole(rolesArray = []) {
  if (
    Array.isArray(rolesArray) &&
    rolesArray.some((r) => r.toUpperCase() === "ADMIN")
  ) {
    return "admin";
  }
  return "user";
}

// LOGIN API - performs request and stores token + user in localStorage/cookie and redux
export const loginUser = async (data) => {
  const res = await postRequest(`${BASE_URL}/auth/login`, data);

  // Support responses where payload is nested in data.data or data
  const payload = res?.data || {};
  const dataNode = payload.data || payload;

  // handle both object and array forms
  const info = Array.isArray(dataNode) ? dataNode[0] : dataNode;
  const token = info?.token || info?.accessToken || null;
  const user = {
    userId: info?.userId || info?.id || null,
    username: info?.username || info?.name || null,
    email: info?.email || null,
  };
  const roles = info?.roles || [];
  const role = determineRole(roles);

  if (token) {
    const decoded = parseJwt(token) || {};
    const expSec = decoded.exp || null;
    const expiresAt = expSec ? new Date(expSec * 1000).toISOString() : null;

    // persist to localStorage and cookie
    persistAuth({ token, user, roles, role, expiresAt });
    localStorage.setItem("userId", decoded.userId);

    // update redux
    store.dispatch(loginSuccess({ token, user, roles, role, expiresAt }));

    // schedule automatic logout
    if (expiresAt) scheduleLogout(new Date(expiresAt).getTime());
  }

  return res;
};

export const registerUser = async (data) => {
  const res = await postRequest(`${BASE_URL}/auth/register`, data);

  // If register returns token (auto-login), reuse same flow
  const payload = res?.data || {};
  const dataNode = payload.data || payload;
  const info = Array.isArray(dataNode) ? dataNode[0] : dataNode;
  const token = info?.token || info?.accessToken || null;
  const user = {
    userId: info?.userId || info?.id || null,
    username: info?.username || info?.name || null,
    email: info?.email || null,
  };
  const roles = info?.roles || [];
  const role = determineRole(roles);

  if (token) {
    const decoded = parseJwt(token) || {};
    const expSec = decoded.exp || null;
    const expiresAt = expSec ? new Date(expSec * 1000).toISOString() : null;
    persistAuth({ token, user, roles, role, expiresAt });
    localStorage.setItem("userId", decoded.userId);
    store.dispatch(loginSuccess({ token, user, roles, role, expiresAt }));
    if (expiresAt) scheduleLogout(new Date(expiresAt).getTime());
  }

  return res;
};

// GET ALL ANNOUNCEMENTS API
export const getAllAnnouncements = async () => {
  const res = await getRequest(`${BASE_URL}/announcements`);
  return res;
};

// ADD ANNOUNCEMENT API
export const addAnnouncement = async (data) => {
  const res = await postRequest(`${BASE_URL}/announcements`, data, {
    headers: getAuthHeaders(),
  });

  return res;
};

// DELETE EXPIRED ANNOUNCEMENTS API
export const deleteExpiredAnnouncements = async () => {
  const res = await deleteRequest(`${BASE_URL}/announcements/expired`, {
    headers: getAuthHeaders(),
  });

  return res;
};

// DELETE ANNOUNCEMENT API
export const deleteAnnouncement = async (payload) => {
  const res = await deleteRequest(`${BASE_URL}/announcements/${payload.id}`, {
    headers: getAuthHeaders(),
  });

  return res;
};

// EDIT ANNOUNCEMENT API
export const editAnnouncement = async (id, data) => {
  const res = await putRequest(`${BASE_URL}/announcements/${id}`, data, {
    headers: getAuthHeaders(),
  });

  return res;
};

// GET ANNOUNCEMENT BY ID API
export const getAnnouncementById = async (id) => {
  const res = await getRequest(`${BASE_URL}/announcements/${id}`);

  return res;
};

// GET ALL CATEGORIES API
export const getAllCategories = async () => {
  const res = await getRequest(`${BASE_URL}/categories`);
  return res;
};

// Get CATEGORY DETAILS BY ID API
export const getCategoryById = async (id) => {
  const res = await getRequest(`${BASE_URL}/categories/${id}`);
  return res;
};

// EDIT CATEGORY API
export const editCategory = async (id, data) => {
  const res = await putRequest(`${BASE_URL}/categories/${id}`, data, {
    headers: getAuthHeaders(),
  });
  return res;
};

// DELETE CATEGORY API
export const deleteCategory = async (categoryId) => {
  const res = await deleteRequest(`${BASE_URL}/categories/${categoryId}`, {
    headers: getAuthHeaders(),
  });
  return res;
};

// ADD CATEGORY API
export const addCategory = async (data) => {
  const res = await postRequest(`${BASE_URL}/categories`, data, {
    headers: getAuthHeaders(),
  });

  return res;
};

// GET SERVERS BY CATEGORY ID API
export const getServersByCategoryId = async (categoryId) => {
  const res = await getRequest(`${BASE_URL}/servers/category/${categoryId}`);
  return res;
};

// GET FEATURED SERVERS API
export const getFeaturedServers = async () => {
  const res = await getRequest(`${BASE_URL}/servers/featured`);
  return res;
};

// ADD SERVER API
export const addServer = async (data) => {
  const res = await postRequest(`${BASE_URL}/servers`, data, {
    headers: getAuthHeaders(),
  });

  return res;
};

// EDIT SERVER API
export const editServer = async (id, data) => {
  const res = await putRequest(`${BASE_URL}/servers/${id}`, data, {
    headers: getAuthHeaders(),
  });

  return res;
};

// DELETE SERVER API
export const deleteServer = async (serverId) => {
  const res = await deleteRequest(`${BASE_URL}/servers/${serverId}`, {
    headers: getAuthHeaders(),
  });

  return res;
};

// GET ALL USERS API (ADMIN)
export const getAllUsersApi = () => {
  const res = getRequest(`${BASE_URL}/admin/users`, {
    headers: getAuthHeaders(),
  });

  return res;
};

// ADMIN ACTIONS API (MAKE ADMIN)
export const makeAdminApi = (userId) => {
  const res = putRequest(`${BASE_URL}/admin/users/${userId}/make-admin`, {}, {
    headers: getAuthHeaders(),
  });

  return res;
};

// ADMIN ACTIONS API (REMOVE ADMIN)
export const removeAdminApi = (userId) => {
  const res = putRequest(`${BASE_URL}/admin/users/${userId}/remove-admin`, {}, {
    headers: getAuthHeaders(),
  });

  return res;
};

// ADMIN ACTIONS API (DELETE USER)
export const deleteUserApi = (userId) => {
  const res =  deleteRequest(`${BASE_URL}/admin/users/${userId}`, {
    headers: getAuthHeaders(),
  });

  return res;
};
